import { useCallback, useEffect, useRef, useState } from 'react';

const getValueFromStorage = <T>(localStorageValue: string|null): T|undefined => {
    if (localStorageValue) {
        try {
            return JSON.parse(localStorageValue);
        } catch (e) {
        }
    }
    return undefined;
};

type UpdateFunction<T> = (v: T) => void | ((f: (c: T) => T) => void);

const getStorageKey = (k: string) => `gmtk23dd_${k}`;

type EvPayload<T> = { key: string, value: T };

export const useSetting = <T>(key: string, defaultValue: T): [T, UpdateFunction<T>] => {
    const [ value, setValue ] = useState<T|undefined>(undefined);
    const currentValue = useRef<T|undefined>(undefined);
    const storageKey = getStorageKey(key);

    useEffect(() => {
        const localStorageItem = window.localStorage.getItem(storageKey);
        const localStorageValue = getValueFromStorage<T>(localStorageItem);
        setValue(localStorageValue);
        currentValue.current = localStorageValue;

        const eventListener: EventListener = ((e: CustomEvent<EvPayload<T>>) => {
            if (e.detail.key === storageKey) {
                setValue(e.detail.value);
                currentValue.current = e.detail.value;
            }
        }) as EventListener;

        document.addEventListener('storageUpdate', eventListener);

        return () => document.removeEventListener('storageUpdate', eventListener);
    }, [ storageKey ]);

    const update = useCallback<UpdateFunction<T>>(updater => {
        let value: T;
        if (typeof updater === 'function') {
            value = updater(currentValue.current ?? defaultValue);
        } else {
            value = updater;
        }
        setValue(value);
        const ev = new CustomEvent<EvPayload<T>>('storageUpdate', {
            detail: {
                key: storageKey,
                value: value,
            },
        });
        document.dispatchEvent(ev);
        window.localStorage.setItem(storageKey, JSON.stringify(value));
    }, [ storageKey, defaultValue ]);

    return [
        value === undefined ? defaultValue : value,
        update,
    ];
};
